import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-tree>` says about itself.
 *
 * Two examples, because this piece answers two shapes that look alike and are not. The rail is the
 * one it was built to replace — a Setup menu hand-assembled row by row — and is not collapsible:
 * every section is always open, because a navigation rail that hides where you can go is worse than
 * one that is long. The hierarchy is the other: a structure a person reads rather than navigates, and
 * the one case where opening and closing earns its keep (CAOS-591).
 */
const docs: ComponentDocs = {
  aliases: ['hierarchy', 'nav rail', 'sidebar menu', 'nested list', 'org chart', 'folder tree'],

  answers: [
    'a left navigation rail',
    'a menu of sections a person picks from',
    'a grouped or nested list of choices',
    'a hierarchy of records, each under its parent',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'seam'],

  inputs: [
    {
      name: 'nodes',
      type: 'json',
      description:
        'The tree itself — a list of nodes, each with an id and a label. A node carrying children is a ' +
        'branch, rendering as a heading over its own nested list (to any depth); a node with none is a ' +
        'leaf, rendering as a row a person can choose. A node may also carry an icon, an href and a ' +
        'badge. Cannot ride an attribute, so it is set as a property.',
      required: true,
    },
    {
      name: 'selectedId',
      attribute: 'selected-id',
      type: 'text',
      description:
        'The id of the currently-active row. Marks it `.is-active` and `aria-current="page"`, and gives ' +
        "it the component's single tab stop, so tabbing in lands where the reader already is.",
    },
    {
      name: 'collapsible',
      attribute: 'collapsible',
      type: 'boolean',
      description:
        'Lets a reader open and close branches. Every branch still starts OPEN: a hierarchy is placed to ' +
        'be seen, so nothing is hidden on arrival. Without this the tree holds every branch open and ' +
        'says nothing about expansion, which is what a permanently open branch should say rather than ' +
        'claiming to be openable.',
      default: false,
    },
    {
      name: 'expandedIds',
      type: 'json',
      description:
        'Which branches are open, as ids — readable to save a reader’s place, settable to restore it. ' +
        'Setting it closes every branch not named. A branch that arrives in new data is open, because it ' +
        'is not something the reader chose to close. Only meaningful alongside collapsible. Cannot ride ' +
        'an attribute, so it is set as a property.',
    },
    {
      name: 'filter',
      attribute: 'filter',
      type: 'text',
      description:
        'Narrows the tree to rows whose label contains this text (case-insensitive). A branch survives ' +
        'only while one of its rows still matches, and every branch is held open while a filter runs, so ' +
        'a match is never left behind a closed one. Closed branches are remembered, not discarded: ' +
        'clearing the filter returns the tree to the shape the reader left it in.',
    },
    {
      name: 'heading',
      type: 'json',
      description:
        'The rail’s heading — `{ label, icon?, href?, id? }`. The console’s name, its mark, and the way ' +
        'back to its landing; it reads as current while that landing is what is on screen. Omitted ⇒ a ' +
        'bare list with no heading. Cannot ride an attribute, so it is set as a property.',
    },
    {
      name: 'filterable',
      attribute: 'filterable',
      type: 'boolean',
      description:
        'Draw a find box above the list, which narrows the list as somebody types. Without it the list ' +
        'is narrowed only by whatever the host puts in `filter`.',
      default: false,
    },
    {
      name: 'findLabel',
      attribute: 'find-label',
      type: 'text',
      description: 'What the find box is called for a screen reader.',
      default: 'Quick Find',
    },
    {
      name: 'findPlaceholder',
      attribute: 'find-placeholder',
      type: 'text',
      description: 'The prompt inside the find box before anything is typed.',
      default: 'Quick Find…',
    },
    {
      name: 'emptyFilterMessage',
      type: 'json',
      description:
        'A function of the filter text, wording "nothing matches" for what the host is filtering. This ' +
        'piece knows only that a filter came up empty, not what it is a filter over — so the host names ' +
        'that, not the tree. Cannot ride an attribute, so it is set as a property.',
      default: 'No matches for "<term>".',
    },
  ],

  events: [
    {
      name: 'tree-choose',
      when: 'A leaf is chosen — by click, or by Enter on it.',
      carries: '`{ id, href? }` — the id of the row that was chosen, and its href when it declared one.',
    },
    {
      name: 'tree-toggle',
      when: 'A reader opens or closes a branch, by clicking its heading or by keyboard.',
      carries: '`{ id, expanded }` — the branch, and whether it is now open. Only a collapsible tree raises it.',
    },
  ],

  state: [],

  slots: [],

  tokens: [
    {
      token: '--caos-state-selected-bg',
      controls: 'The fill behind the current row. The platform’s selected value (CAOS-586), not this piece’s own.',
    },
    { token: '--caos-state-hover-bg', controls: 'The fill behind a row under the pointer.' },
  ],

  parts: [],

  methods: [],

  placing:
    'A tree is placed wherever a hierarchy is read: a navigation rail down the side of a console or an ' +
    "app, such as Setup's own section list, or a structure on a page — roles under the role above them, " +
    'categories under their parent. It carries no routing logic of its own: a leaf with an `href` ' +
    'renders as a real link (middle-click, ctrl/cmd-click and "open in new tab" all work as expected) ' +
    'and a leaf without one renders as a button; either way, the host decides what "chosen" does by ' +
    'listening for `tree-choose`. Add `collapsible` when the tree is deep enough that a reader will want ' +
    'to put part of it away, and leave it off for a rail, where every destination should stay in sight.',

  whenToUse:
    'Any list of things a person picks from or reads that is grouped or nested — a navigation rail, a ' +
    'section menu, a picker over categories, a hierarchy of records under their parents. Reach for it ' +
    'instead of hand-building rows, so the grouping, the indentation, the active-row highlight, the ' +
    'icons, the badges, the filter and the keyboard all behave the same everywhere they appear.',

  insteadUse: [
    { name: 'SegmentedFilter', when: 'A flat row of a few mutually-exclusive choices, not a grouped or nested list.' },
    { name: 'Breadcrumb', when: "Showing where a person already is, rather than offering a list to choose from." },
    {
      name: 'DataTable',
      when: 'The rows carry columns of their own. A table with expandable rows keeps the columns and still nests; a tree has one label per node.',
    },
  ],

  accessibility:
    'The whole tree is one tab stop, not one per row. Inside it the arrow keys move focus: down and up ' +
    'through the rows on screen, right to open a closed branch and then into it, left to close an open ' +
    'branch and then out to its parent, Home and End to the first and last, and typing letters jumps to ' +
    'the next label starting with them. Enter opens a leaf; Enter or Space opens or closes a branch. ' +
    'Every node is a `treeitem` inside a `tree`, each carrying its level, how many siblings it has and ' +
    'which one it is, and each branch holds its children in its own `group` — so a branch is announced ' +
    'as standing over its rows rather than merely beside them. A collapsible branch says whether it is ' +
    'open; one that cannot be closed says nothing, rather than claiming an ability it does not have. ' +
    'The active row is marked `aria-current="page"`.',

  examples: [
    {
      key: 'console-rail',
      label: 'A whole console side navigation',
      summary:
        'The rail as a console places it: a heading leading back to the landing, a find box that narrows the list as somebody types, and the sections beneath it.',
      placement: {
        component: 'caos-tree',
        inputs: {
          heading: { label: 'Setup', icon: 'settings', href: '/setup' },
          filterable: true,
          findPlaceholder: 'Quick Find…',
          selectedId: 'roles',
          nodes: [
            {
              id: 'admin',
              label: 'Administration',
              children: [
                { id: 'permission-sets', label: 'Permission Sets', icon: 'shield-check', href: '/setup/permission-sets' },
                { id: 'roles', label: 'Roles', icon: 'shield-check', href: '/setup/roles' },
              ],
            },
            {
              id: 'objects',
              label: 'Objects & Fields',
              children: [{ id: 'object-manager', label: 'Object Manager', icon: 'boxes', href: '/setup/object-manager' }],
            },
          ],
        },
      },
    },
    {
      key: 'setup-rail',
      label: 'A Setup-style rail',
      summary:
        'Two groups — Administration and Objects & Fields — with an icon on most rows, a "Soon" badge ' +
        'on one that is not live yet, and Object Manager pre-selected as the active row. Not collapsible: ' +
        'a rail keeps every destination in sight.',
      placement: {
        component: 'caos-tree',
        inputs: {
          selectedId: 'object-manager',
          nodes: [
            {
              id: 'admin',
              label: 'Administration',
              children: [
                { id: 'permission-sets', label: 'Permission Sets', icon: 'shield-check' },
                { id: 'roles', label: 'Roles', icon: 'shield-check' },
              ],
            },
            {
              id: 'objects',
              label: 'Objects & Fields',
              children: [
                { id: 'object-manager', label: 'Object Manager', icon: 'boxes' },
                { id: 'value-sets', label: 'Value Sets', icon: 'list', badge: 'Soon' },
              ],
            },
          ],
        },
      },
    },
    {
      key: 'role-hierarchy',
      label: 'A hierarchy that can be put away',
      summary:
        'Roles under the role above them, each badged with how many people hold it, every branch a link ' +
        'to its own record. Collapsible, so a deep branch can be closed — and open on arrival, so the ' +
        'whole shape is visible first.',
      placement: {
        component: 'caos-tree',
        inputs: {
          collapsible: true,
          nodes: [
            {
              id: 'ceo',
              label: 'CEO',
              badge: '1 user',
              children: [
                { id: 'ops', label: 'Ops', badge: 'No users', href: '/setup/roles/ops' },
                {
                  id: 'sales_vp',
                  label: 'Sales VP',
                  badge: 'No users',
                  children: [{ id: 'sales_rep', label: 'Sales Rep', badge: '3 users', href: '/setup/roles/sales_rep' }],
                },
              ],
            },
          ],
        },
      },
    },
  ],
};

export default docs;
